/**
 * OnboardingGuard Component
 *
 * Ensures users have completed onboarding before accessing protected routes.
 * - Shows loading state while fetching onboarding status
 * - Redirects to /onboarding if profile is incomplete
 * - Renders children when onboarding is complete
 *
 * Note: This guard should be used INSIDE AuthGuard, not as a replacement.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useGetOnboardingProfileQuery } from '../../store/api/onboardingApi';

interface OnboardingGuardProps {
  children?: React.ReactNode;
}

export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { data, isLoading, isError } = useGetOnboardingProfileQuery();

  // Show loading state while fetching onboarding status
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto" />
          <p className="mt-4 text-gray-500">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // If there's an error, allow access rather than blocking forever
  // The actual pages can handle showing appropriate error states
  if (isError) {
    return <>{children ?? <Outlet />}</>;
  }

  // Check if onboarding is complete
  // User needs both a profile AND to have completed the onboarding flow
  const hasCompletedOnboarding = data?.hasCompletedOnboarding ?? false;
  const hasProfile = data?.profile !== null && data?.profile !== undefined;

  if (!hasCompletedOnboarding || !hasProfile) {
    return <Navigate to="/onboarding" replace />;
  }

  // Onboarding complete - render children
  return <>{children ?? <Outlet />}</>;
}
